/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import {
  ApiError,
  GuaranteeConfigStruture,
  PayloadRequestGarantias,
  PropertyTypeStrutureCatalog,
  StatusCatalog,
} from "@interfaces/index";
import ControlledPlaceAutocomplete from "@components/ControlledPlaceAutocomplete";
import { useEffect, useState } from "react";
import { useLazyGetPropertyTypeQuery } from "@api/catalogs";
import { useSelector } from "react-redux";
import { useLazyGetGuaranteesDataQuery, usePostGuaranteesTypesMutation } from "@api/garantiasApi";
import { usePostValidateNextStageMutation, usePutApplicationCompletedMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { setIsDisabledGuaranteeTypeSelect } from "@store/slices/registerSlice";

// Datos de prueba Eliniminar despues
const TIPOS_ACTIVO_FIJO = [
  {
    id: "5f9cb0d7-4345-4653-b7fd-7cd278b5ac19",
    name: "Maquinaria industrial",
    description: "Activos utilizados en procesos industriales y de manufactura.",
    code: "MAQ_IND",
  },
  {
    id: "bf11af8a-2fe6-4bac-99de-217b1e90a58c",
    name: "Equipo de producción",
    description: "Herramientas y equipos empleados para la producción de bienes.",
    code: "EQ_PROD",
  },
  {
    id: "1ada8d0f-0303-475e-85f7-44397ff9088b",
    name: "Flotillas vehiculares",
    description: "Conjunto de vehículos utilizados para operaciones empresariales.",
    code: "FLOT_VEH",
  },
  {
    id: "515c9db8-d1e2-432f-a1a4-4f4cc1da8b64",
    name: "Tractos y transporte pesado",
    description: "Vehículos especializados para transporte de carga o maquinaria.",
    code: "TRANS_PES",
  },
  {
    id: "245efd80-eade-46b3-bb45-20ac3c74cb4e",
    name: "Equipo médico",
    description: "Instrumentos y dispositivos destinados a la atención de salud.",
    code: "EQ_MED",
  },
  {
    id: "5f9cb0d4-4345-4653-b7fd-7cd278b5ac19",
    name: "Tecnología y telecomunicaciones",
    description: "Equipos y sistemas tecnológicos o de comunicación.",
    code: "TEC_TELE",
  },
];

// Datos de prueba Eliniminar despues
const TIPOS_FLUJOS_O_CONTRATOS = [
  {
    id: "5f9cb0d7-4345-4653-b7fd-7cd278b5ac19",
    name: "Contrato de suministro o servicio",
    description: "Acuerdos para proveer bienes o servicios de forma periódica.",
    code: "CON_SUM",
  },
  {
    id: "bf11af8a-2fe6-4bac-99de-217b1e90a58c",
    name: "Contrato de arrendamiento",
    description: "Convenios para el uso temporal de bienes a cambio de pago.",
    code: "CON_ARR",
  },
  {
    id: "1ada8d0f-0303-475e-85f7-44397ff9088b",
    name: "Contrato gubernamental",
    description: "Contratos celebrados con entidades o dependencias públicas.",
    code: "CON_GOB",
  },
  {
    id: "515c9db8-d1e2-432f-a1a4-4f4cc1da8b64",
    name: "Cuentas por cobrar",
    description: "Derechos de cobro derivados de ventas o servicios a crédito.",
    code: "CTA_COB",
  },
  {
    id: "245efd80-eade-46b3-bb45-20ac3c74cb4e",
    name: "Ingresos por concesión o franquicia",
    description: "Flujos provenientes de la explotación de concesiones o franquicias.",
    code: "ING_CONC",
  },
  {
    id: "5f9cb0d4-4345-4653-b7fd-7cd278b5ac19",
    name: "Derechos de cobro en fideicomiso",
    description: "Derechos económicos administrados o garantizados por fideicomisos.",
    code: "DER_FIDE",
  },
];

interface FormGarantiaInmobiliariaProps {
  nextStatus: StatusCatalog | null;
  guarantee_type_id: string | null;
  guaranteeConfigList: GuaranteeConfigStruture[];
  onSeleccion: (guarantee_type: string) => void;
}

const FormGarantiaInmobiliaria = ({
  nextStatus,
  guarantee_type_id,
  guaranteeConfigList,
  onSeleccion,
}: FormGarantiaInmobiliariaProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const isDisabled = useSelector((state: RootState) => state.register.is_disabled_guarantee_type);

  const [triggerListPropertyType] = useLazyGetPropertyTypeQuery();
  const [getGuaranteesData] = useLazyGetGuaranteesDataQuery();
  const [postGuarantees] = usePostGuaranteesTypesMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [putApplicationCompleted] = usePutApplicationCompletedMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [estimatedValue, setEstimatedValue] = useState(0);
  const [listPropertys, setListPropertys] = useState<PropertyTypeStrutureCatalog[]>([]);

  const [listPropertys_list, setListPropertys_list] = useState<PropertyTypeStrutureCatalog[]>([]);

  const [label, setLabel] = useState<string>("Inmueble");

  const formik = useFormik({
    initialValues: {
      type_inmobiliaria: "",
      valor_estimado: "",
      ubicacion: "",
      latitud: "",
      longitud: "",
      guarantee_type: guarantee_type_id || "",
    },
    validationSchema: yup.object().shape({
      type_inmobiliaria: yup.string().required("Campo requerido"),
      valor_estimado: yup.string().required("Campo requerido"),
      ubicacion: yup.string().required("Campo requerido"),
      guarantee_type: yup.string().required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        const payLoad: PayloadRequestGarantias = {
          guarantees_list: [
            {
              loan_application_id: application.id,
              property_type_id: values.type_inmobiliaria,
              estimated_value: estimatedValue,
              latitude: Number(values.latitud),
              longitude: Number(values.longitud),
              address: values.ubicacion,
            },
          ],
        };

        await postGuarantees({ loan_application_id: application.id, data: payLoad }).unwrap();

        await putApplicationCompleted({
          id_sol: application.id,
          body: { guarantee_type_id: values.guarantee_type },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setIsDisabledGuaranteeTypeSelect({ disabled: true }));
        navigate(nextStatus?.url);
      } catch (error) {
        const api_error = error as ApiError;

        Sentry.captureException(error, {
          extra: {
            component: "InfoGarantiaInmobiliaria",
            values: formik.values,
          },
        });

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const [response_property_type, response_garantia_data] = await Promise.all([
          triggerListPropertyType().unwrap(),
          getGuaranteesData({ loan_application_id: application.id }).unwrap(),
        ]);
        setListPropertys_list(response_property_type);

        if (response_garantia_data.length > 0) {
          const typeGuuarantee = response_garantia_data.find((item) => item.property_type_id !== null);
          const num = Number(typeGuuarantee?.estimated_value);
          setEstimatedValue(num);
          const formatted = num.toLocaleString("en-US");
          formik.setFieldValue("valor_estimado", formatted || 0);
          formik.setFieldValue("type_inmobiliaria", typeGuuarantee?.property_type_id);
          formik.setFieldValue("latitude", typeGuuarantee?.latitude);
          formik.setFieldValue("longitud", typeGuuarantee?.longitude);
          formik.setFieldValue("ubicacion", typeGuuarantee?.address);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "Garantias-form-inmobiliaria",
            values: formik.values,
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    const numericValue = Number(rawValue);

    setEstimatedValue(numericValue);

    const formattedValue = numericValue.toLocaleString("en-US");
    formik.setFieldValue("valor_estimado", formattedValue);
  };

  const setListTypeInmobiliaria = (guarantee_type_id: string, list_default: PropertyTypeStrutureCatalog[]) => {
    const selectedGuarantee = guaranteeConfigList.find((item) => item.guarantee_type_id === guarantee_type_id);

    if (selectedGuarantee && selectedGuarantee.guarantee_type.code === "GCFC") {
      setLabel("Activo Fijo");
      return setListPropertys(TIPOS_FLUJOS_O_CONTRATOS);
    }

    if (selectedGuarantee && selectedGuarantee.guarantee_type.code === "GTGPR") {
      setLabel("Activo Fijo");
      return setListPropertys(TIPOS_ACTIVO_FIJO);
    }

    return setListPropertys(list_default);
  };

  useEffect(() => {
    setListTypeInmobiliaria(formik.values.guarantee_type, listPropertys_list);
  }, [formik.values.guarantee_type, listPropertys_list]);

  return (
    <Box>
      <form onSubmit={formik.handleSubmit} noValidate>
        <FormControl
          fullWidth
          required
          sx={{
            ...customStylesAsterisk,
            mt: 2,
          }}
          error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
        >
          <InputLabel id="guarantee_type">Tipo de Garantía</InputLabel>
          <Select
            labelId="guarantee_type"
            id="guarantee_type"
            name="guarantee_type"
            fullWidth
            required
            label="Tipo de Garantía"
            value={formik.values.guarantee_type}
            onBlur={formik.handleBlur}
            onChange={(e) => {
              formik.handleChange(e);
              onSeleccion(e.target.value);
            }}
            error={formik.touched.guarantee_type && Boolean(formik.errors.guarantee_type)}
            disabled={isLoading || formik.isSubmitting || isDisabled}
          >
            <MenuItem value={""}>Selecciona</MenuItem>
            {guaranteeConfigList.map((item) => (
              <MenuItem key={item.id} value={item.guarantee_type_id}>
                {item.guarantee_type.name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.guarantee_type && formik.errors.guarantee_type && (
            <FormHelperText>{formik.errors.guarantee_type}</FormHelperText>
          )}
        </FormControl>

        <Typography
          mt={isMobileOrTablet ? 2 : 3}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          Detalle de tu garantía
        </Typography>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="type_inmobiliaria-label" required>
            Tipo de {label}
          </InputLabel>
          <Select
            labelId="type_inmobiliaria-label"
            id="type_inmobiliaria"
            name="type_inmobiliaria"
            fullWidth
            required
            label={`Tipo de ${label}`}
            value={formik.values.type_inmobiliaria}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.type_inmobiliaria && Boolean(formik.errors.type_inmobiliaria)}
            disabled={formik.isSubmitting || isLoading}
          >
            <MenuItem value={""}>Selecciona</MenuItem>

            {listPropertys?.map((property) => (
              <MenuItem key={property.id} value={property.id}>
                {property.name}
              </MenuItem>
            ))}
          </Select>
          {Boolean(formik.touched.type_inmobiliaria) && Boolean(formik.errors.type_inmobiliaria) && (
            <FormHelperText>{formik.errors.type_inmobiliaria}</FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth>
          <TextField
            id="valor_estimado"
            name="valor_estimado"
            label="Valor Estimado"
            margin="normal"
            fullWidth
            autoComplete="off"
            onBlur={formik.handleBlur}
            onChange={handleChangeValue}
            inputProps={{ maxLength: 20 }}
            value={formik.values.valor_estimado}
            disabled={formik.isSubmitting || isLoading}
            error={formik.touched.valor_estimado && Boolean(formik.errors.valor_estimado)}
            helperText={formik.touched.valor_estimado ? formik.errors.valor_estimado : ""}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        </FormControl>

        <ControlledPlaceAutocomplete
          value={{ address: formik.values.ubicacion, lat: 0, lng: 0 }}
          onChange={(newValue) => {
            formik.setFieldValue("ubicacion", newValue.address);
            formik.setFieldValue("latitud", newValue.lat);
            formik.setFieldValue("longitud", newValue.lng);
          }}
          onBlur={() => formik.setFieldTouched("ubicacion", true)}
          error={formik.touched.ubicacion && Boolean(formik.errors.ubicacion)}
          helperText={formik.touched.ubicacion ? formik.errors.ubicacion : ""}
          disabled={formik.isSubmitting || isLoading}
        />

        <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
          <Button variant="default" type="submit" color="primary" disabled={formik.isSubmitting || isLoading}>
            Continuar
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default FormGarantiaInmobiliaria;
